import type { Lens } from '@hookform/lenses';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { SwitchInputGroup } from '@interxfi/design-system/components/input/switch-input/group/switch-input-group';
import type { IxcRegistrationV1IndividualW9 } from '@interxfi/types';
import { AppDatePickerInputWithLens } from '../../../../../components/form/app-date-picker-input';
import { AppSwitchInputWithLens } from '../../../../../components/form/app-switch-input';
import { W9_BACKUP_WITHHOLDING_TOOLTIP, W9_CERTIFICATION_TOOLTIP } from '../../tooltips';

export const individualProfileW9FormSchema = z.object({
  isSubjectToBackupWithholding: z.boolean(),
  certification: z.literal(true, { error: 'Certification is required' }),
  signed: z.iso.date().nonempty('Signature date is required'),
});

export type IndividualProfileW9FormValues = z.infer<typeof individualProfileW9FormSchema>;

export const individualProfileW9FormDefaultValues: IndividualProfileW9FormValues = {
  isSubjectToBackupWithholding: false,
  certification: false as true, // weird, but has to be true to pass validation. It will be set by the user.
  signed: '',
};

export function getInitialFormValuesForIndividualW9(data: IxcRegistrationV1IndividualW9 | undefined): IndividualProfileW9FormValues {
  return {
    ...individualProfileW9FormDefaultValues,
    isSubjectToBackupWithholding: data?.isSubjectToBackupWithholding ?? individualProfileW9FormDefaultValues.isSubjectToBackupWithholding,
    certification: (data?.certification ?? individualProfileW9FormDefaultValues.certification) as true, // once again, weird but required to satisfy the schema
    signed: data?.signed ?? '',
  };
}

export function formValuesToIndividualW9(values: IndividualProfileW9FormValues): IxcRegistrationV1IndividualW9 {
  return {
    isSubjectToBackupWithholding: values.isSubjectToBackupWithholding,
    certification: values.certification,
    signed: values.signed,
  };
}

interface IndividualProfileW9FormProps {
  lens: Lens<IndividualProfileW9FormValues>;
}

export function IndividualProfileW9Form({ lens }: IndividualProfileW9FormProps) {
  return (
    <VerticalStack gap={4}>
      <SwitchInputGroup>
        <AppSwitchInputWithLens
          info={W9_BACKUP_WITHHOLDING_TOOLTIP}
          label="Subject to Backup Withholding"
          lens={lens.focus('isSubjectToBackupWithholding')}
        />
        <AppSwitchInputWithLens info={W9_CERTIFICATION_TOOLTIP} label="Certification" lens={lens.focus('certification')} required />
      </SwitchInputGroup>

      <AppDatePickerInputWithLens label="Signed" lens={lens.focus('signed')} required />
    </VerticalStack>
  );
}
